import { computed, observable, observableArray } from 'knockout-decorators'
import { marked } from 'marked'

import { Background } from './pages/background'
import { Page } from './pages/page'

import './components/page-sidebar/page-sidebar'
import './components/subpage-header/subpage-header'

import './refund.less'
import { Language, Translations } from './utils'
import {
  Offer,
  Person,
  RefundChoice,
  RefundOption,
  RefundProductOption,
  RefundRequest,
  Ticket,
} from '@tixa/schema'
import type { Order } from '@tixa/schema'
import * as ko from 'knockout'
import { pageState } from '~/store/page'
import { AxiosError } from 'axios'

export class RefundOrderPage extends Page {
  @observable public email = ''
  @observable public orderId = ''

  @observable public isLoading = false

  @observable public isVerified = false
  @observable public hasOrder = false
  @observable public isRefundSuccessful = false
  @observable public confirmationCode = ''
  @observable public order?: Order = undefined

  @observableArray public ticketRefunds: {
    id: string
    percentage: number
  }[] = []

  @observableArray public exchangedTickets: string[] = []
  @observableArray public chosenProducts: Map<
    string,
    {
      offerId: string
      offerName: string
      variation: string
      variationName: string
    }
  > = new Map<
    string,
    {
      offerId: string
      offerName: string
      variation: string
      variationName: string
    }
  >()
  @observableArray public showProductsForTickets: string[] = []
  @observableArray public showAddressForTickets: string[] = []
  @observable public canSubmitRequest = false
  @observableArray private refundChoices: string[] = []
  @observableArray private refundChoiceDisplay: string[] = []
  private exchangeChoices: Map<string, string> = new Map<string, string>()
  private exchanges: object = {}
  private choices: object = {}

  @observable private overview: {
    id: string
    choice: string
    choiceDetail: string
    price: string
    numericPrice: number
  }[] = []

  @observable public name = ''
  @observable public zip = ''
  @observable public city = ''
  @observable public address = ''
  @observable public phone = ''
  @observable public changes = 0

  @observable public refundRequestId = ''
  @observable public feedback = ''

  @computed get amountToRefund(): string {
    if (!this.order || this.order.tickets.length === 0) {
      return ''
    }
    const currency = this.order.tickets[0].priceCurrency || 'HUF'

    const amount = this.overview
      .map((ticket) => ticket.numericPrice)
      .reduce((a, b) => a + b, 0)

    return Language.formatCurrency(amount, currency)
  }

  @computed get markedCustomContent(): string | undefined {
    if (this.order && this.order.customMessage) {
      const renderer = new marked.Renderer()
      return marked.parse(this.order.customMessage, { renderer })
    }
    return undefined
  }

  constructor() {
    super()
    Background.getInstance().setDefaultBackground()
    if (window.location.search.length > 1) {
      const queryParts = window.location.search.substring(1).split('/')
      if (queryParts[0] && !isNaN(parseInt(queryParts[0], 10))) {
        this.orderId = queryParts[0]
      }
      if (queryParts[1]) {
        this.confirmationCode = queryParts[1]
        this.isVerified = true
        this.confirm()
      }
    }
  }

  public async verify(): Promise<void> {
    this.isLoading = true
    try {
      await pageState.api.refund.check(this.email, this.orderId)
      this.feedback = ''
      this.isVerified = true
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response && e.response.data) {
          this.feedback =
            typeof e.response.data === 'string'
              ? e.response.data
              : JSON.stringify(e.response.data)
          return
        }
        if (e.response && e.response.status === 403) {
          this.feedback = Translations.refundalreadyrequested
          return
        }
      }
      this.feedback = Translations.refundnotickets
      console.error('Unexpected error during refund verification:', e)
    }
  }

  public async confirm(): Promise<void> {
    try {
      const order = await pageState.api.refund.confirm(
        this.orderId,
        this.confirmationCode
      )
      this.feedback = ''
      this.order = order
      order.tickets.forEach((ticket) => {
        if (!ticket.returnOptions) {
          ticket.returnOptions = [RefundOption.Keep, RefundOption.Refund]
        }
        this.refundChoices.push('none')
        this.refundChoiceDisplay.push('')
        if (ticket.exchangeOptions) {
          this.exchanges[ticket.id] = ko.observable(
            ticket.exchangeOptions[0]['@id']
          )
        }
        this.choices[ticket.id] = ko.observable('none')
      })
      // this.updateSubmitButtonState();
      this.hasOrder = true
    } catch (e) {
      this.order = undefined
      this.hasOrder = false

      if (e instanceof AxiosError) {
        if (e.response && e.response.data) {
          this.feedback =
            typeof e.response.data === 'string'
              ? e.response.data
              : JSON.stringify(e.response.data)
          return
        }
        if (e.response && e.response.status === 403) {
          this.feedback = Translations.refundalreadyrequested
          return
        }
      }
      this.feedback = Translations.refundcantconfirm
      console.error('Unexpected error during refund confirmation:', e)
    }
  }

  @computed({ pure: false })
  get isConfirmDisabled(): boolean {
    const didntMakeAllChoices = this.refundChoices.some(
      (choice) => choice === 'none'
    )
    const didntChooseProductVariatons =
      this.showProductsForTickets.length !== this.chosenProducts.size
    const formIsIncomplete =
      this.showAddressForTickets.length > 0 &&
      !(
        this.name.length > 2 &&
        this.zip.length > 3 &&
        this.city.length > 1 &&
        this.address.length > 2 &&
        this.phone.length > 7
      )

    return (
      didntMakeAllChoices || didntChooseProductVariatons || formIsIncomplete
    )
  }

  private getChoiceOverview(): {
    id: string
    choice: string
    choiceDetail: string
    price: string
    numericPrice: number
  }[] {
    const tickets = this.order?.tickets
    const choiceDisplays = this.refundChoiceDisplay
    const exchangedTickets = this.exchangedTickets
    const exchanges = this.exchanges

    const currency = this.order?.tickets[0].priceCurrency

    const ticketById = (id: string) => {
      return this.order?.tickets.find((t) => t.id === id)
    }

    const getOption = (ticketId: string, offerId: string) => {
      const ticket = ticketById(ticketId)
      if (ticket && ticket.exchangeOptions) {
        return ticket.exchangeOptions.find(
          (option) => option['@id'].toString() === offerId.toString()
        )
      }
      return undefined
    }

    const ret = tickets?.map((ticket, index) => {
      const choiceDetails: string[] = []

      if (exchangedTickets.includes(ticket.id)) {
        const option = getOption(ticket.id, exchanges[ticket.id]())
        if (option) {
          choiceDetails.push(option.name)
        }
      }
      if (this.showProductsForTickets.includes(ticket.id)) {
        choiceDetails.push(
          `${this.chosenProducts.get(ticket.id)?.offerName ?? ''} - ${
            this.chosenProducts.get(ticket.id)?.variationName ?? ''
          }`
        )
      }

      let refundPercentage = 0
      const refund = this.ticketRefunds.find((t) => t.id === ticket.id)
      if (refund && refund.percentage) {
        refundPercentage = refund.percentage
      }

      const ticketDetails = ticketById(ticket.id)
      const price =
        ticketDetails && ticketDetails.price
          ? ticketDetails.price * refundPercentage
          : 0

      return {
        id: ticket.barcode,
        choice: choiceDisplays[index],
        choiceDetail: choiceDetails.join(' / '),
        price: Language.formatCurrency(price, currency || 'HUF'),
        numericPrice: price,
      }
    })
    return ret || []
  }

  public async checkRequest(): Promise<void> {
    const refundRequest = this.createRequest()
    try {
      await pageState.api.refund.checkRefund(refundRequest)
      this.feedback = ''
      this.overview = this.getChoiceOverview()
      window.scrollTo(0, 0)
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response && e.response.data) {
          this.feedback =
            typeof e.response.data === 'string'
              ? e.response.data
              : JSON.stringify(e.response.data)
          return
        }
      }
      this.feedback = Translations.refundcantprocess
      console.error('Unexpected error during refund check request:', e)
    }
  }

  public backToForm(): void {
    this.overview = []
    window.scrollTo(0, 0)
  }

  private createRequest(): RefundRequest {
    const ticketMap = new Map(
      this.refundChoices.map((choice, index) => {
        return [this.order?.tickets[index].id, choice]
      })
    )
    const exchangeMap = new Map(
      Object.entries(this.exchanges).map((value) => {
        return [value[0], (value[1] as () => string)()]
      })
    )
    const choicesToSubmit: RefundChoice[] = (this.order?.tickets ?? []).map(
      (ticket) => {
        const offers: { offerId: string; variation?: string }[] = []

        if (ticketMap.get(ticket.id) === RefundOption.Exchange) {
          const offerId = exchangeMap.get(ticket.id)
          if (offerId) {
            offers.push({ offerId })
          }
        }

        if (this.chosenProducts.get(ticket.id)) {
          const product = this.chosenProducts.get(ticket.id)
          offers.push({
            offerId: product?.offerId ?? '',
            variation: product?.variation,
          })
        }

        const ret: RefundChoice = {
          ticketId: ticket.id,
          action: ticketMap.get(ticket.id) || '',
          exchangeTo: offers.length > 0 ? offers : undefined,
        }
        return ret
      }
    )
    let person: Person = {
      '@type': 'Person',
      '@id': '',
      name: '',
      telephone: '',
      email: '',
      address: {
        '@type': 'Address',
        postalCode: '',
        streetAddress: '',
        addressLocality: '',
        addressCountry: '',
        addressRegion: '',
        countryCode: '',
      },
    }
    if (this.chosenProducts.size > 0) {
      person = {
        '@type': 'Person',
        '@id': '',
        name: this.name,
        telephone: this.phone,
        email: this.email,
        address: {
          '@type': 'Address',
          postalCode: this.zip,
          streetAddress: this.address,
          addressLocality: this.city,
          addressCountry: '',
          addressRegion: '',
          countryCode: '',
        },
      }
    }
    const refundRequest: RefundRequest = {
      orderId: this.order?.id.toString() || '',
      confirmationCode: this.confirmationCode,
      tickets: choicesToSubmit,
      person,
    }
    return refundRequest
  }

  public async submitRequest(): Promise<void> {
    const refundRequest = this.createRequest()
    try {
      const request = await pageState.api.refund.submit(refundRequest)
      this.feedback = ''
      this.isRefundSuccessful = true
      this.refundRequestId = request.refundId
      window.scrollTo(0, 0)
    } catch (e) {
      if (e instanceof AxiosError) {
        if (e.response && e.response.data) {
          this.feedback =
            typeof e.response.data === 'string'
              ? e.response.data
              : JSON.stringify(e.response.data)
          return
        }
      }
      this.feedback = Translations.refundcantprocess
      console.error('Unexpected error during refund submission:', e)
    }
  }

  public formatPrice(price: number, currency: string): string {
    if (!price || !currency) {
      return Translations.unknownprice
    }
    return Language.formatCurrency(price, currency)
  }

  public returnOptionsFor(ticket: Ticket): { id: string; display: string }[] {
    const standardOptions = (ticket.returnOptions ?? []).map((option) => {
      return {
        id: option as string,
        display: Translations[`refundoption${option}`] as string,
      }
    })
    const customOptions = ticket.customReturnOptions || []
    return [...standardOptions, ...customOptions]
  }

  public changeChoice(ticket: Ticket, e: InputEvent): void {
    const element = e.target as HTMLSelectElement
    const value = element.value as RefundOption

    if (value !== RefundOption.Exchange) {
      this.exchangedTickets = this.exchangedTickets.filter(
        (exchangedTicketId) => exchangedTicketId !== ticket.id
      )
    } else {
      this.exchangedTickets.push(ticket.id)
    }

    this.ticketRefunds = this.ticketRefunds.filter((t) => t.id !== ticket.id)
    let refundPercentage = 0

    if (ticket.customReturnOptions) {
      const returnOption = ticket.customReturnOptions.find(
        (option) => option.id === value
      )
      if (returnOption && returnOption.refundPercentage) {
        refundPercentage = returnOption.refundPercentage
      }
    }

    if (value === RefundOption.Refund) {
      refundPercentage = 1
    }

    this.ticketRefunds.push({ id: ticket.id, percentage: refundPercentage })

    if (
      ticket.customReturnOptions &&
      ticket.customReturnOptions.find(
        (option) => option.id === value && option.showProducts
      )
    ) {
      if (!this.showProductsForTickets.includes(ticket.id)) {
        this.showProductsForTickets.push(ticket.id)
      }
    } else {
      this.showProductsForTickets = this.showProductsForTickets.filter(
        (ticketId) => ticketId !== ticket.id
      )
      this.chosenProducts.delete(ticket.id)
    }

    if (
      ticket.customReturnOptions &&
      ticket.customReturnOptions.find(
        (option) =>
          option.id === value &&
          option.showProducts &&
          option.showProducts !== RefundProductOption.WithoutAddress
      )
    ) {
      if (!this.showAddressForTickets.includes(ticket.id)) {
        this.showAddressForTickets.push(ticket.id)
      }
    } else {
      this.showAddressForTickets = this.showAddressForTickets.filter(
        (ticketId) => ticketId !== ticket.id
      )
    }

    const itemIndex = this.order?.tickets.indexOf(ticket)

    if (!itemIndex) {
      console.error('Ticket not found in order tickets.')
      return
    }

    this.refundChoices.splice(itemIndex, 1, value)
    this.refundChoiceDisplay[itemIndex] =
      element.options[element.selectedIndex].text

    this.changes += 1
  }

  public changeProductVariation(
    offer: Offer,
    e: InputEvent,
    ticketId: string
  ): void {
    const value = (e.target as HTMLSelectElement).value
    this.chosenProducts.set(ticketId, {
      offerId: offer['@id'],
      offerName: offer.name,
      variation: value,
      variationName:
        offer.variations?.find((v) => v.id === value)?.display ?? '',
    })
    this.changes += 1
  }
}
